import { ACCOUNT_TYPE_LABELS } from '@bcube/shared/constants';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Building2, Info } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod/v3';
import { economicGroupApi, type IEconomicGroup } from '@/api/commercial/economic-groups';
import {
  type CnpjaMappedCompany,
  type CnpjaNormalizedCompany,
  type CnpjaSearchItem,
  cnpjaApi,
} from '@/api/integrations/cnpja';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type LocalAccount,
  type LocalAddress,
  type LocalContact,
  useCreateAccountFlow,
} from '@/context/create-account-flow';
import { useDebounceFn } from '@/hooks/use-debounce';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';
import { isValidCNPJ, isValidCPF, maskCpfCnpj, maskRg } from '@/lib/formatters';

const schema = z
  .object({
    document: z.object({
      value: z.string(),
      type: z.enum(['cpf', 'cnpj']),
    }),
    cpf: z
      .object({
        name: z
          .string({ required_error: 'Nome completo é obrigatório' })
          .min(1, 'Nome completo é obrigatório')
          .refine((val) => val.trim().split(/\s+/).length >= 2, {
            message: 'Informe o nome completo',
          }),
        rg: z
          .string()
          .regex(
            /^[A-Za-z]{0,2}[-\s.]?\d{1,2}\.?\d{3}\.?\d{3}[-\s.]?[A-Za-z0-9]{0,2}$/,
            'RG deve conter apenas números, pontos, barras e hífen',
          )
          .min(5, 'RG muito curto')
          .max(20, 'RG muito longo')
          .optional()
          .or(z.literal('')),
      })
      .optional(),
    cnpj: z
      .object({
        social_name: z
          .string({ required_error: 'Razão social é obrigatória' })
          .min(1, 'Razão social é obrigatória'),
        fantasy_name: z.string().optional(),
        state_registration: z
          .string()
          .max(14, 'Inscrição Estadual deve ter no máximo 14 dígitos')
          .optional(),
        municipal_registration: z
          .string()
          .max(15, 'Inscrição Municipal deve ter no máximo 15 dígitos')
          .optional(),
        status: z.string().optional(),
        situationIE: z
          .object({
            id: z.string().optional(),
            status: z.boolean().optional(),
            text: z.string().optional(),
          })
          .optional(),
        typeIE: z.string().optional(),
        sector: z.string({ required_error: 'Setor obrigatório' }).min(1, 'Setor obrigatório'),
        accountType: z.enum(['MEI', 'ME', 'EPP', 'DEMAIS', 'PF']).optional(),
        simeiSince: z.string().optional(),
        natureText: z.string().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    const type = data.document.type;

    if (type === 'cpf') {
      if (!data.cpf) {
        ctx.addIssue({
          path: ['cpf'],
          code: z.ZodIssueCode.custom,
          message: 'Dados de CPF são obrigatórios',
        });
        return;
      }
      if (!data.cpf.name || data.cpf.name.trim().length < 1) {
        ctx.addIssue({
          path: ['cpf', 'name'],
          code: z.ZodIssueCode.custom,
          message: 'Nome completo é obrigatório e deve ter ao menos duas palavras',
        });
      }
    }

    if (type === 'cnpj') {
      if (!data.cnpj) {
        ctx.addIssue({
          path: ['cnpj'],
          code: z.ZodIssueCode.custom,
          message: 'Dados de CNPJ são obrigatórios',
        });
        return;
      }
      if (!data.cnpj.social_name || data.cnpj.social_name.trim().length < 1) {
        ctx.addIssue({
          path: ['cnpj', 'social_name'],
          code: z.ZodIssueCode.custom,
          message: 'Razão social é obrigatória',
        });
      }
      if (!data.cnpj.sector || data.cnpj.sector.trim().length < 1) {
        ctx.addIssue({
          path: ['cnpj', 'sector'],
          code: z.ZodIssueCode.custom,
          message: 'Setor obrigatório',
        });
      }
    }
  });

type FormSchema = z.infer<typeof schema>;

export function StepAccount() {
  const navigate = useNavigate();
  const [_searchParams] = useSearchParams();
  const { toast } = useToast();
  const _queryClient = useQueryClient();

  const {
    createAccountLocally,
    createAddressLocally,
    createContactLocally,
    createEconomicGroupLocally,
    account: localAccount,
    economicGroup: localEconomicGroup,
  } = useCreateAccountFlow();

  const [type, setType] = useState<'cpf' | 'cnpj' | undefined>(undefined);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonText, setButtonText] = useState<'Validar' | 'Editar'>('Validar');
  const [dataCnpj, setDataCnpj] = useState<CnpjaMappedCompany | null>(null);
  const [sectors, setSectors] = useState<Array<{ id: string; name: string }>>([]);

  const [disabledFields, setDisabledFields] = useState({
    social_name: false,
    fantasy_name: false,
    status: false,
    state_registration: false,
    municipal_registration: false,
    typeIE: false,
    accountType: false,
  });

  // Document search state (CPF/CNPJ combobox) - union type for both search results
  type DocumentSearchItem = CnpjaNormalizedCompany | CnpjaSearchItem;
  const [documentSearchResults, setDocumentSearchResults] = useState<DocumentSearchItem[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<DocumentSearchItem | null>(null);
  const [isSearchingDocument, setIsSearchingDocument] = useState(false);

  // Economic group state - DB-based holding search
  const [holdingSearchResults, setHoldingSearchResults] = useState<IEconomicGroup[]>([]);
  const [selectedHoldingGroup, setSelectedHoldingGroup] = useState<IEconomicGroup | null>(null);
  const [isSearchingHolding, setIsSearchingHolding] = useState(false);
  const [existingEconomicGroup, setExistingEconomicGroup] = useState<{
    group: IEconomicGroup;
    role: 'holding' | 'controlada';
  } | null>(null);

  const methods = useForm<FormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      document: { value: '', type: 'cpf' },
    },
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = methods;

  // Load sectors
  useEffect(() => {
    const loadSectors = async () => {
      try {
        const { data } = await api.get('/commercial/sectors');

        if (data?.data) {
          setSectors(data.data.filter((s: { enabled: boolean }) => s.enabled));
        }
      } catch (error) {
        console.warn('Failed to load sectors:', error);
        // Provide default sectors if API fails
        setSectors([
          { id: '1', name: 'Comercio' },
          { id: '2', name: 'Industria' },
          { id: '3', name: 'Servicos' },
        ]);
      }
    };
    loadSectors();
  }, []);

  // Document search function (CPF/CNPJ combobox)
  const handleDocumentSearch = useCallback(async (value: string) => {
    if (!value.trim()) {
      setDocumentSearchResults([]);
      return;
    }

    const cleanedValue = value.replace(/\D/g, '');

    // If it looks like a CPF (11 digits), don't search CNPJA
    if (cleanedValue.length === 11) {
      setDocumentSearchResults([]);
      return;
    }

    setIsSearchingDocument(true);
    try {
      let results: DocumentSearchItem[];

      // If the value is a valid CNPJ (14 digits), search by exact CNPJ
      if (cleanedValue.length === 14 && isValidCNPJ(cleanedValue)) {
        const company = await cnpjaApi.findByCnpj(cleanedValue);
        results = company ? [company] : [];
      } else if (value.length >= 3) {
        // Search by company name if at least 3 characters
        results = await cnpjaApi.findByName(value);
      } else {
        results = [];
      }

      setDocumentSearchResults(results);
    } catch (error) {
      console.error('Document search error:', error);
      setDocumentSearchResults([]);
    } finally {
      setIsSearchingDocument(false);
    }
  }, []);

  // Debounced document search
  const debouncedDocumentSearch = useDebounceFn(handleDocumentSearch, 500);

  // Auto-detect economic group when document is validated
  const checkExistingEconomicGroup = useCallback(
    async (taxId: string) => {
      try {
        const result = await economicGroupApi.findByTaxId(taxId);
        if (result.found && result.economicGroup) {
          setExistingEconomicGroup({
            group: result.economicGroup,
            role: result.role!,
          });
          // Store in context as auto-detected
          createEconomicGroupLocally({
            existingGroupId: result.economicGroup.id,
            economic_group_holding: result.economicGroup.economic_group_holding,
            economic_group_controlled: result.economicGroup.economic_group_controlled,
            autoDetected: true,
          });
        } else {
          setExistingEconomicGroup(null);
        }
      } catch (error) {
        console.warn('Failed to check existing economic group:', error);
        setExistingEconomicGroup(null);
      }
    },
    [createEconomicGroupLocally],
  );

  // Handle document selection from combobox
  const handleDocumentSelect = useCallback(
    async (selected: DocumentSearchItem[]) => {
      const item = selected.length > 0 ? selected[0] : null;

      if (!item) {
        setSelectedDocument(null);
        setType(undefined);
        setDataCnpj(null);
        setDisabledFields({
          social_name: false,
          fantasy_name: false,
          status: false,
          state_registration: false,
          municipal_registration: false,
          typeIE: false,
          accountType: false,
        });
        return;
      }

      setSelectedDocument(item);
      setIsValidating(true);

      try {
        const cleanedTaxId = item.taxId.replace(/\D/g, '');

        // Check if account already exists
        try {
          const { data: existsData } = await api.get(`/commercial/accounts/exists/${cleanedTaxId}`);
          if (existsData?.exists) {
            setError('document.value', {
              type: 'manual',
              message: 'Documento já cadastrado!',
            });
            setSelectedDocument(null);
            setIsValidating(false);
            return;
          }
        } catch {
          // Continue if check fails
        }

        // Set document value
        setValue('document.value', maskCpfCnpj(cleanedTaxId));
        setValue('document.type', 'cnpj');
        setType('cnpj');

        // Fetch registration + normalized data in parallel
        const [registrationRes, normalizedRes] = await Promise.all([
          api.get(`/integrations/cnpja/${cleanedTaxId}/registration`),
          api.get(`/integrations/cnpja/${cleanedTaxId}/normalized`).catch(() => ({ data: null })),
        ]);
        const normalized = normalizedRes.data?.data?.metadata?.company ?? {};
        const data = registrationRes.data?.data;

        if (data) {
          setDataCnpj(data);

          setValue('cnpj.fantasy_name', data.alias ?? '');
          setValue('cnpj.social_name', data.company?.name ?? data.alias ?? '');
          setValue('cnpj.state_registration', data.registrations?.[0]?.number ?? '');
          setValue('cnpj.status', data.registrations?.[0]?.status?.text ?? '');

          const situationIE = {
            id: data.registrations?.[0]?.enabled ? '1' : '2',
            text: data.registrations?.[0]?.enabled ? 'Habilitada' : 'Não habilitada',
            status: data.registrations?.[0]?.enabled,
          };
          setValue('cnpj.situationIE', situationIE);
          setValue('cnpj.typeIE', data.registrations?.[0]?.type?.text ?? '');

          // Determine accountType from normalized CNPJA data
          let accountType: 'MEI' | 'ME' | 'EPP' | 'DEMAIS' | undefined;
          if (normalized.simei?.optant) {
            accountType = 'MEI';
            if (normalized.simei.since || data.company?.simei?.since) {
              setValue('cnpj.simeiSince', normalized.simei.since ?? data.company.simei.since);
            }
          } else if (data.company?.simei?.since) {
            // Fallback: registration has simei.since → MEI
            accountType = 'MEI';
            setValue('cnpj.simeiSince', data.company.simei.since);
          } else {
            accountType = normalized.size?.acronym as 'ME' | 'EPP' | 'DEMAIS' | undefined;
          }
          if (accountType) {
            setValue('cnpj.accountType', accountType);
          }
          // Store natureText from normalized data
          if (normalized.nature?.text) {
            setValue('cnpj.natureText', normalized.nature.text);
          }

          setDisabledFields({
            social_name: Boolean(data.company?.name),
            fantasy_name: true,
            status: Boolean(data.registrations?.[0]?.status?.text),
            state_registration: Boolean(data.registrations?.[0]?.number),
            municipal_registration: true,
            typeIE: Boolean(data.registrations?.[0]?.type?.text),
            accountType: Boolean(accountType),
          });
        }

        setButtonText('Editar');
        clearErrors('document.value');

        // Auto-detect economic group for CNPJ
        checkExistingEconomicGroup(cleanedTaxId);
      } catch (error) {
        console.error('Error fetching document data:', error);
        toast({
          title: 'Erro',
          description: 'Erro ao buscar dados do CNPJ',
          variant: 'destructive',
        });
      } finally {
        setIsValidating(false);
      }
    },
    [setValue, setError, clearErrors, toast, checkExistingEconomicGroup],
  );

  // Economic group search function - searches DB economic groups
  const handleHoldingSearch = useCallback(async (value: string) => {
    if (!value.trim()) {
      setHoldingSearchResults([]);
      return;
    }

    setIsSearchingHolding(true);
    try {
      const result = await economicGroupApi.findMany({ search: value, limit: 10 });
      setHoldingSearchResults(result.docs || []);
    } catch (error) {
      console.error('Economic group search error:', error);
      setHoldingSearchResults([]);
    } finally {
      setIsSearchingHolding(false);
    }
  }, []);

  const debouncedHoldingSearch = useDebounceFn(handleHoldingSearch, 500);

  // Handle holding group selection
  const handleHoldingGroupSelect = useCallback(
    (selected: IEconomicGroup[]) => {
      const item = selected.length > 0 ? selected[0] : null;
      setSelectedHoldingGroup(item);

      if (item) {
        createEconomicGroupLocally({
          existingGroupId: item.id,
          economic_group_holding: item.economic_group_holding,
          economic_group_controlled: item.economic_group_controlled,
        });
      } else {
        createEconomicGroupLocally({
          existingGroupId: undefined,
          economic_group_holding: undefined,
          economic_group_controlled: undefined,
        });
      }
    },
    [createEconomicGroupLocally],
  );

  // Initialize form with existing account data when component mounts
  const hasRestoredRef = useRef(false);
  useEffect(() => {
    if (!localAccount || hasRestoredRef.current) return;
    hasRestoredRef.current = true;

    setType(localAccount.document.type);
    setButtonText('Editar');
    setDisabledFields({
      social_name: true,
      fantasy_name: true,
      status: true,
      state_registration: true,
      municipal_registration: true,
      typeIE: true,
      accountType: true,
    });

    setValue('document.value', localAccount.document.value);
    setValue('document.type', localAccount.document.type);

    if (localAccount.document.type === 'cpf') {
      if (localAccount.name) {
        setValue('cpf.name', localAccount.name);
      }
      if (localAccount.rg) {
        setValue('cpf.rg', maskRg(localAccount.rg));
      }
    }

    if (localAccount.document.type === 'cnpj') {
      if (localAccount.social_name) {
        setValue('cnpj.social_name', localAccount.social_name);
      }
      if (localAccount.fantasy_name) {
        setValue('cnpj.fantasy_name', localAccount.fantasy_name);
      }
      if (localAccount.state_registration) {
        setValue('cnpj.state_registration', localAccount.state_registration);
      }
      if (localAccount.municipal_registration) {
        setValue('cnpj.municipal_registration', localAccount.municipal_registration);
      }
      if (localAccount.status) {
        setValue('cnpj.status', localAccount.status);
      }
      if (localAccount.setor && localAccount.setor.length > 0) {
        setValue('cnpj.sector', localAccount.setor[0]);
      }
      if (localAccount.situationIE) {
        setValue('cnpj.situationIE', localAccount.situationIE);
      }
      if (localAccount.typeIE) {
        setValue('cnpj.typeIE', localAccount.typeIE);
      }
      if (localAccount.accountType) {
        setValue('cnpj.accountType', localAccount.accountType);
      }
    }
  }, [localAccount, setValue]);

  const handleValidateDocument = async () => {
    setIsValidating(true);
    try {
      const value = getValues('document.value');
      const cleanedValue = value.replace(/\D/g, '');

      if (!cleanedValue) {
        setError('document.value', {
          type: 'manual',
          message: 'Documento não informado!',
        });
        return;
      }

      // Check if account exists
      try {
        const { data: existsData } = await api.get(`/commercial/accounts/exists/${cleanedValue}`);
        if (existsData?.exists) {
          setError('document.value', {
            type: 'manual',
            message: 'Documento já cadastrado!',
          });
          return;
        }
      } catch {
        // Continue if check fails
      }

      let newType: 'cpf' | 'cnpj' | undefined;

      if (cleanedValue.length === 11) {
        const isValid = isValidCPF(cleanedValue);
        if (!isValid) {
          setError('document.value', {
            type: 'manual',
            message: 'CPF inválido!',
          });
          return;
        }
        newType = 'cpf';
      } else if (cleanedValue.length === 14) {
        const isValid = isValidCNPJ(cleanedValue);
        if (!isValid) {
          setError('document.value', {
            type: 'manual',
            message: 'CNPJ inválido!',
          });
          return;
        }
        newType = 'cnpj';
      } else {
        setError('document.value', {
          type: 'manual',
          message: 'Documento inválido! CPF deve ter 11 dígitos, CNPJ deve ter 14 dígitos.',
        });
        return;
      }

      if (newType === 'cnpj') {
        // Fetch registration + normalized data in parallel
        try {
          const [registrationRes, normalizedRes] = await Promise.all([
            api.get(`/integrations/cnpja/${cleanedValue}/registration`),
            api.get(`/integrations/cnpja/${cleanedValue}/normalized`).catch(() => ({ data: null })),
          ]);
          const normalized = normalizedRes.data?.data?.metadata?.company ?? {};
          const data = registrationRes.data?.data;

          if (data) {
            setDataCnpj(data);

            setValue('cnpj.fantasy_name', data.alias ?? '');
            setValue('cnpj.social_name', data.company?.name ?? data.alias ?? '');
            setValue('cnpj.state_registration', data.registrations?.[0]?.number ?? '');
            setValue('cnpj.status', data.registrations?.[0]?.status?.text ?? '');

            const situationIE = {
              id: data.registrations?.[0]?.enabled ? '1' : '2',
              text: data.registrations?.[0]?.enabled ? 'Habilitada' : 'Não habilitada',
              status: data.registrations?.[0]?.enabled,
            };
            setValue('cnpj.situationIE', situationIE);
            setValue('cnpj.typeIE', data.registrations?.[0]?.type?.text ?? '');

            // Determine accountType from normalized CNPJA data
            let accountType: 'MEI' | 'ME' | 'EPP' | 'DEMAIS' | undefined;
            if (normalized.simei?.optant) {
              accountType = 'MEI';
              if (normalized.simei.since || data.company?.simei?.since) {
                setValue('cnpj.simeiSince', normalized.simei.since ?? data.company.simei.since);
              }
            } else if (data.company?.simei?.since) {
              // Fallback: registration has simei.since → MEI
              accountType = 'MEI';
              setValue('cnpj.simeiSince', data.company.simei.since);
            } else {
              accountType = normalized.size?.acronym as 'ME' | 'EPP' | 'DEMAIS' | undefined;
            }
            if (accountType) {
              setValue('cnpj.accountType', accountType);
            }
            // Store natureText from normalized data
            if (normalized.nature?.text) {
              setValue('cnpj.natureText', normalized.nature.text);
            }

            setDisabledFields({
              social_name: Boolean(data.company?.name),
              fantasy_name: true,
              status: Boolean(data.registrations?.[0]?.status?.text),
              state_registration: Boolean(data.registrations?.[0]?.number),
              municipal_registration: true,
              typeIE: Boolean(data.registrations?.[0]?.type?.text),
              accountType: Boolean(accountType),
            });
          }
        } catch (error) {
          console.warn('Failed to fetch CNPJ data:', error);
          // Continue without CNPJ data
        }
      }

      setValue('document.type', newType);
      setType(newType);
      clearErrors('document.value');
      setButtonText('Editar');

      // Auto-detect economic group for CNPJ
      if (newType === 'cnpj') {
        checkExistingEconomicGroup(cleanedValue);
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleEditDocument = () => {
    setButtonText('Validar');
    setType(undefined);
    setSelectedDocument(null);
    setDocumentSearchResults([]);
    setDataCnpj(null);
    setExistingEconomicGroup(null);
    setSelectedHoldingGroup(null);
    setValue('document.value', '');

    // Clear all CNPJ-populated fields
    setValue('cnpj.social_name', '');
    setValue('cnpj.fantasy_name', '');
    setValue('cnpj.state_registration', '');
    setValue('cnpj.status', '');
    setValue('cnpj.situationIE', undefined);
    setValue('cnpj.typeIE', '');
    setValue('cnpj.accountType', undefined);
    setValue('cnpj.simeiSince', '');
    setValue('cnpj.natureText', '');
    setValue('cnpj.municipal_registration', '');
    setValue('cnpj.sector', '');

    // Clear CPF fields
    setValue('cpf.name', '');
    setValue('cpf.rg', '');

    setDisabledFields({
      social_name: false,
      fantasy_name: false,
      status: false,
      state_registration: false,
      municipal_registration: false,
      typeIE: false,
      accountType: false,
    });
    clearErrors('document.value');
  };

  const handleButtonClick = async () => {
    if (buttonText === 'Validar') {
      await handleValidateDocument();
    } else {
      handleEditDocument();
    }
  };

  const onSubmit = async (data: FormSchema) => {
    setIsSubmitting(true);

    try {
      const address = dataCnpj?.address;
      const phones = dataCnpj?.phones || [];
      const emails = dataCnpj?.emails || [];

      const accountLocalId = crypto.randomUUID();

      const base: LocalAccount = {
        id: accountLocalId,
        document: {
          ...data.document,
          value: data.document.value,
        },
        ...(type === 'cpf'
          ? {
              name: data.cpf?.name,
              rg: data.cpf?.rg || '',
              accountType: 'PF' as const,
            }
          : {
              social_name: data.cnpj?.social_name,
              fantasy_name: data.cnpj?.fantasy_name,
              state_registration: data.cnpj?.state_registration,
              municipal_registration: data.cnpj?.municipal_registration,
              status: data.cnpj?.status,
              situationIE: data.cnpj?.situationIE || {},
              typeIE: data.cnpj?.typeIE,
              setor: data.cnpj?.sector ? [data.cnpj?.sector] : undefined,
              accountType: data.cnpj?.accountType,
              simeiSince: data.cnpj?.simeiSince,
              natureText: data.cnpj?.natureText,
            }),
      };

      createAccountLocally(base);

      // Create address from CNPJ data
      if (address?.city && address.state) {
        const newAddress: LocalAddress = {
          id: crypto.randomUUID(),
          city: address.city,
          state: address.state,
          street: address.street ?? '',
          district: address.district ?? '',
          number: address.number ?? '',
          zip_code: address.zip ?? '',
          complement: address.details ?? '',
          type: ['Fiscal'],
          default_address: true,
        };

        createAddressLocally(newAddress);
      }

      // Create contacts for each phone
      if (phones.length > 0 && dataCnpj) {
        phones.forEach((phone) => {
          if (!phone.area || !phone.number) return;
          const newContact: LocalContact = {
            id: crypto.randomUUID(),
            name: dataCnpj.company?.name || dataCnpj.alias || '',
            contractEnabled: false,
            positionOrRelation: '',
            taxId: dataCnpj.taxId,
            contactFor: ['Fiscal'],
            contactItems: [
              {
                id: crypto.randomUUID(),
                contact: `${phone.area}${phone.number}`,
                type: phone.type === 'LANDLINE' ? 'Telefone Comercial' : 'Celular',
                preferredContact: {},
              },
            ],
            originType: 'api',
          };
          createContactLocally(newContact);
        });
      }

      // Create contacts for each email
      if (emails.length > 0 && dataCnpj) {
        emails.forEach((email) => {
          if (!email.address) return;
          const newContact: LocalContact = {
            id: crypto.randomUUID(),
            name: dataCnpj.company?.name || dataCnpj.alias || '',
            contractEnabled: false,
            positionOrRelation: '',
            taxId: dataCnpj.taxId,
            contactFor: ['Fiscal'],
            contactItems: [
              {
                id: crypto.randomUUID(),
                contact: email.address,
                type: 'Email',
                preferredContact: {},
              },
            ],
            originType: 'api',
          };
          createContactLocally(newContact);
        });
      }

      navigate('/commercial/accounts/create?step=addresses');
    } catch (error) {
      console.error('Error in onSubmit:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao criar conta localmente',
        variant: 'destructive',
      });

      setIsSubmitting(false);
    }
  };

  const hasValidated = type && getValues('document.type') === type;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Dados da Conta</CardTitle>
      </CardHeader>
      <CardContent>
        <FormProvider {...methods}>
          <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
            {/* Document Input - Combobox with CNPJ search */}
            <div className="space-y-2">
              <div className="flex items-end gap-4">
                <div className="flex-1 space-y-2">
                  <Label>CPF/CNPJ *</Label>
                  {buttonText === 'Editar' ? (
                    // Show disabled input when document is selected
                    <Input
                      value={getValues('document.value') || ''}
                      disabled
                      placeholder="Documento selecionado"
                    />
                  ) : (
                    // Show Combobox for search
                    <Combobox<DocumentSearchItem>
                      data={documentSearchResults}
                      value={selectedDocument ? [selectedDocument] : []}
                      keyExtractor={(item) => item.taxId}
                      displayValueGetter={(item) => `${item.name} (${maskCpfCnpj(item.taxId)})`}
                      placeholder="Digite o CPF, CNPJ, Nome, Razão Social ou Nome Fantasia..."
                      type="single"
                      isLoading={isSearchingDocument}
                      disableLocalFilter
                      onSearchChange={(query) => {
                        // Also update the document value for CPF manual entry
                        const cleanedValue = query.replace(/\D/g, '');
                        if (cleanedValue.length <= 14) {
                          setValue('document.value', maskCpfCnpj(query));
                        }
                        debouncedDocumentSearch(query);
                      }}
                      onOptionChange={handleDocumentSelect}
                    />
                  )}
                  {errors.document?.value && (
                    <p className="text-sm text-red-500">{errors.document.value.message}</p>
                  )}
                </div>
                <Button type="button" disabled={isValidating} onClick={handleButtonClick}>
                  {isValidating ? 'Validando...' : buttonText}
                </Button>
              </div>

              {type && (
                <div className="space-y-2">
                  <Label>Tipo de pessoa</Label>
                  <Input disabled value={type === 'cpf' ? 'Pessoa física' : 'Pessoa jurídica'} />
                </div>
              )}
            </div>

            {/* CPF Form Fields */}
            {hasValidated && type === 'cpf' && !isValidating && (
              <div className="flex flex-col gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cpf-name">Nome completo *</Label>
                  <Input
                    id="cpf-name"
                    placeholder="Digite o nome completo"
                    {...register('cpf.name')}
                  />
                  {errors.cpf?.name && (
                    <p className="text-sm text-red-500">{errors.cpf.name.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cpf-rg">RG</Label>
                  <Controller
                    control={control}
                    name="cpf.rg"
                    render={({ field }) => (
                      <Input
                        id="cpf-rg"
                        placeholder="Digite o RG"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(maskRg(e.target.value))}
                      />
                    )}
                  />
                  {errors.cpf?.rg && (
                    <p className="text-sm text-red-500">{errors.cpf.rg.message}</p>
                  )}
                </div>
              </div>
            )}

            {/* CNPJ Form Fields */}
            {hasValidated && type === 'cnpj' && !isValidating && (
              <div className="flex flex-col gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cnpj-social-name">Razão Social *</Label>
                  <Input
                    id="cnpj-social-name"
                    placeholder="Digite a razão social"
                    {...register('cnpj.social_name')}
                    disabled={disabledFields.social_name}
                  />
                  {errors.cnpj?.social_name && (
                    <p className="text-sm text-red-500">{errors.cnpj.social_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj-fantasy-name">Nome Fantasia</Label>
                  <Input
                    id="cnpj-fantasy-name"
                    placeholder="Digite o nome fantasia"
                    {...register('cnpj.fantasy_name')}
                    disabled={disabledFields.fantasy_name}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj-state-registration">Inscrição Estadual</Label>
                  <Controller
                    control={control}
                    name="cnpj.state_registration"
                    render={({ field }) => (
                      <Input
                        id="cnpj-state-registration"
                        placeholder="Digite a inscrição estadual"
                        value={field.value || ''}
                        onChange={(e) => {
                          const formatted = e.target.value.replace(/\D/g, '');
                          field.onChange(formatted);
                        }}
                        disabled={disabledFields.state_registration}
                      />
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj-status">Situação CNPJ</Label>
                  <Controller
                    control={control}
                    name="cnpj.status"
                    render={({ field }) => (
                      <Input
                        id="cnpj-status"
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Digite a situação do CNPJ"
                        disabled={disabledFields.status}
                      />
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj-typeIE">Tipo IE</Label>
                  <Controller
                    control={control}
                    name="cnpj.typeIE"
                    render={({ field }) => (
                      <Input
                        id="cnpj-typeIE"
                        value={field.value || ''}
                        onChange={field.onChange}
                        placeholder="Digite o tipo IE"
                        disabled={disabledFields.typeIE}
                      />
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj-municipal-registration">Inscrição Municipal</Label>
                  <Controller
                    control={control}
                    name="cnpj.municipal_registration"
                    render={({ field }) => (
                      <Input
                        id="cnpj-municipal-registration"
                        placeholder="Digite a inscrição municipal"
                        value={field.value || ''}
                        onChange={(e) => {
                          const formatted = e.target.value.replace(/\D/g, '');
                          field.onChange(formatted);
                        }}
                        disabled={disabledFields.municipal_registration}
                      />
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj-account-type">Porte</Label>
                  <Input
                    value={
                      watch('cnpj.accountType')
                        ? (ACCOUNT_TYPE_LABELS[watch('cnpj.accountType')!] ??
                          watch('cnpj.accountType'))
                        : ''
                    }
                    disabled
                    readOnly
                    placeholder="Preenchido automaticamente"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj-nature-text">Natureza Jurídica</Label>
                  <Input
                    value={watch('cnpj.natureText') ?? ''}
                    disabled
                    readOnly
                    placeholder="Preenchido automaticamente"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cnpj-sector">
                    Setor <span className="text-red-600">*</span>
                  </Label>
                  <Controller
                    control={control}
                    name="cnpj.sector"
                    render={({ field }) => (
                      <Select onValueChange={field.onChange} value={field.value ?? ''}>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o setor" />
                        </SelectTrigger>
                        <SelectContent>
                          {sectors.map((sector) => (
                            <SelectItem key={sector.id} value={sector.name}>
                              {sector.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.cnpj?.sector && (
                    <p className="text-sm text-red-500">{errors.cnpj.sector.message}</p>
                  )}
                </div>

                {/* Economic Group - Auto-detected or Holding Selection */}
                {existingEconomicGroup ? (
                  <div className="space-y-2">
                    <Label>Grupo Econômico</Label>
                    <div className="rounded-lg border p-4 bg-muted/30 space-y-3">
                      <div className="flex items-center gap-2">
                        <Info className="h-4 w-4 text-blue-500 flex-shrink-0" />
                        <p className="text-sm text-blue-700">
                          Esta empresa já pertence ao grupo econômico da holding{' '}
                          <strong>
                            {existingEconomicGroup.group.economic_group_holding?.name}
                          </strong>
                          {existingEconomicGroup.role === 'holding'
                            ? ' (como holding)'
                            : ' (como controlada)'}
                        </p>
                      </div>

                      {existingEconomicGroup.group.economic_group_holding && (
                        <div className="flex items-center gap-2 p-2 border rounded bg-background">
                          <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">
                              {existingEconomicGroup.group.economic_group_holding.name}
                            </p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {maskCpfCnpj(
                                existingEconomicGroup.group.economic_group_holding.taxId,
                              )}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            Holding
                          </Badge>
                        </div>
                      )}

                      {existingEconomicGroup.group.economic_group_controlled &&
                        existingEconomicGroup.group.economic_group_controlled.length > 0 && (
                          <div className="space-y-1.5">
                            <p className="text-xs font-medium text-muted-foreground uppercase">
                              Controladas (
                              {existingEconomicGroup.group.economic_group_controlled.length})
                            </p>
                            {existingEconomicGroup.group.economic_group_controlled.map((c) => (
                              <div
                                key={c.taxId}
                                className="flex items-center gap-2 p-2 border rounded bg-background"
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm">{c.name}</p>
                                  <p className="text-xs text-muted-foreground font-mono">
                                    {maskCpfCnpj(c.taxId)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Grupo Econômico (Holding)</Label>
                    <Combobox<IEconomicGroup>
                      data={holdingSearchResults}
                      value={selectedHoldingGroup ? [selectedHoldingGroup] : []}
                      keyExtractor={(item) => item.id}
                      displayValueGetter={(item) =>
                        item.economic_group_holding
                          ? `${item.economic_group_holding.name} (${maskCpfCnpj(item.economic_group_holding.taxId)})`
                          : `Grupo ${item.id}`
                      }
                      placeholder="Buscar grupo econômico por nome ou CNPJ..."
                      type="single"
                      isLoading={isSearchingHolding}
                      disableLocalFilter
                      onSearchChange={(query) => debouncedHoldingSearch(query)}
                      onOptionChange={handleHoldingGroupSelect}
                    />
                    {localEconomicGroup?.economic_group_holding && !selectedHoldingGroup && (
                      <p className="text-sm text-muted-foreground">
                        Selecionado: {localEconomicGroup.economic_group_holding.name}
                      </p>
                    )}

                    {/* Preview of selected group */}
                    {selectedHoldingGroup && (
                      <div className="rounded-lg border p-3 bg-muted/20 space-y-2">
                        {selectedHoldingGroup.economic_group_holding && (
                          <div className="flex items-center gap-2 p-2 border rounded bg-background">
                            <Building2 className="h-4 w-4 text-primary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm">
                                {selectedHoldingGroup.economic_group_holding.name}
                              </p>
                              <p className="text-xs text-muted-foreground font-mono">
                                {maskCpfCnpj(selectedHoldingGroup.economic_group_holding.taxId)}
                              </p>
                            </div>
                            <Badge variant="outline" className="text-xs">
                              Holding
                            </Badge>
                          </div>
                        )}

                        {selectedHoldingGroup.economic_group_controlled &&
                          selectedHoldingGroup.economic_group_controlled.length > 0 && (
                            <div className="space-y-1">
                              <p className="text-xs font-medium text-muted-foreground">
                                Controladas existentes:
                              </p>
                              {selectedHoldingGroup.economic_group_controlled.map((c) => (
                                <div
                                  key={c.taxId}
                                  className="flex items-center gap-2 p-2 border rounded bg-background text-sm"
                                >
                                  <span>{c.name}</span>
                                  <span className="text-xs text-muted-foreground font-mono">
                                    {maskCpfCnpj(c.taxId)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}

                        <div className="flex items-center gap-2 p-2 border rounded bg-green-50 border-green-200">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-green-800 font-medium">
                              {getValues('cnpj.social_name') ||
                                getValues('cnpj.fantasy_name') ||
                                'Nova empresa'}
                            </p>
                            <p className="text-xs text-green-600 font-mono">
                              {getValues('document.value')}
                            </p>
                          </div>
                          <Badge
                            className="text-xs bg-green-100 text-green-700 border-green-300"
                            variant="outline"
                          >
                            Nova
                          </Badge>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Submit Buttons */}
            {hasValidated && (
              <div className="flex gap-4 justify-end pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isSubmitting}
                  onClick={() => {
                    navigate('/commercial/accounts');
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-black text-white hover:bg-gray-800"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Salvando...' : 'Salvar e próximo'}
                </Button>
              </div>
            )}
          </form>
        </FormProvider>
      </CardContent>
    </Card>
  );
}
